import React from 'react';
import JobCard from './JobCard';

// Instagram-style shimmer skeleton card
const ShimmerJobCard = () => {
    return (
        <div className="job-card" style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '1.5rem',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Shimmer overlay */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite'
            }}></div>
            
            {/* Job title skeleton */}
            <div style={{
                height: '24px',
                backgroundColor: '#f3f4f6',
                borderRadius: '6px',
                marginBottom: '1rem',
                width: '75%'
            }}></div>
            
            {/* Location and salary skeletons */}
            <div style={{ marginBottom: '1rem' }}>
                <div style={{
                    height: '16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    width: '60%'
                }}></div>
                <div style={{
                    height: '16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    width: '50%'
                }}></div>
            </div>
            
            {/* Skills tags skeleton */}
            <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '1.5rem',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    height: '28px',
                    width: '60px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '14px'
                }}></div>
                <div style={{
                    height: '28px',
                    width: '80px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '14px'
                }}></div>
                <div style={{
                    height: '28px',
                    width: '70px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '14px'
                }}></div>
            </div>
            
            {/* Action buttons skeleton */}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5rem',
                marginBottom: '1.5rem' /* Add 24px bottom spacing */
            }}>
                <div style={{
                    height: '48px',
                    flex: '1',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '12px'
                }}></div>
                <div style={{
                    height: '48px',
                    flex: '1',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '12px'
                }}></div>
            </div>
        </div>
    );
};

const FeaturedJobs = ({ onQuickApply, onViewDetails, onRefer, jobs = [], loading = false, apiBusy = false, loadingMore = false, hasLoaded = false, initialFetchDone = false, error = null, isSearchActive = false, currentLocation = null, savedLocation = null, candidateSkills = [], candidateSkillsWithRatings = [], skillIdToNameMap = {} }) => {
    // Debug current props to verify loader gating
    React.useEffect(() => {
        try {
            // eslint-disable-next-line no-console
            console.debug('[FeaturedJobs] render', { loading, initialFetchDone, hasLoaded, isSearchActive, apiBusy, loadingMore, jobsLen: (jobs || []).length });
        } catch (_) {}
    }, [loading, initialFetchDone, hasLoaded, isSearchActive, apiBusy, loadingMore, jobs]);

    // Determine if we should show empty state - when API is completely done and no jobs
    // Do NOT gate on isSearchActive; we want to show empty for both default and filtered/search states
    const shouldShowEmpty = !apiBusy && !loading && !loadingMore && initialFetchDone && (jobs?.length === 0);
    
    // Log current state for debugging
    React.useEffect(() => {
        if (initialFetchDone && jobs?.length === 0) {
            console.log('[FeaturedJobs] Empty jobs detected', { 
                shouldShowEmpty, 
                apiBusy, 
                loading, 
                loadingMore, 
                initialFetchDone, 
                isSearchActive, 
                jobsLen: jobs?.length 
            });
        }
        if (shouldShowEmpty) {
            console.log('[FeaturedJobs] SHOWING EMPTY STATE');
        }
    }, [shouldShowEmpty, apiBusy, loading, loadingMore, initialFetchDone, isSearchActive, jobs]);

    return (
        <>
            <div className="job-listings-header">
                <h2>
                    {currentLocation?.city 
                        ? `Showing Jobs near ${currentLocation.city}`
                        : 'Showing Jobs near your location'
                    }
                </h2>
            </div>

            <div className="job-data-container">
                <div className="job-cards" style={{
                    position: 'relative',
                    ...(shouldShowEmpty || (initialFetchDone && !loading && !loadingMore && jobs?.length === 0)
                        ? { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }
                        : {})
                }}>
                    {/* Light overlay shimmer band shown while API is busy, even if jobs are present */}
                    {apiBusy && jobs?.length > 0 && !loadingMore && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '6px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            zIndex: 1
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: '-30%',
                                width: '30%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)',
                                animation: 'shimmer 1.5s infinite'
                            }} />
                        </div>
                    )}
                    {(apiBusy || loading || !initialFetchDone) && !loadingMore && jobs.length === 0 ? (
                        // Instagram-style shimmer skeleton cards for initial load (prioritize loading state over error)
                        Array.from({ length: 6 }, (_, index) => (
                            <ShimmerJobCard key={`shimmer-${index}`} />
                        ))
                    ) : jobs.length > 0 ? (
                        // Show actual job data when loaded
                        <>
                            {jobs.map((job, index) => (
                                <JobCard 
                                    key={job.id || index} 
                                    job={job} 
                                    onQuickApply={onQuickApply}
                                    onViewDetails={onViewDetails}
                                    onRefer={onRefer}
                                    candidateSkills={candidateSkills}
                                    candidateSkillsWithRatings={candidateSkillsWithRatings}
                                    skillIdToNameMap={skillIdToNameMap}
                                />
                            ))}
                            {/* Show skeleton cards at the end when loading more */}
                            {loadingMore && Array.from({ length: 3 }, (_, index) => (
                                <ShimmerJobCard key={`loading-more-${index}`} />
                            ))}
                        </>
                    ) : error && !apiBusy && !loading && initialFetchDone ? (
                        // Show error message only when API is not running and has completed
                        <div style={{
                            gridColumn: '1 / -1', // Span all columns
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                            width: '100%'
                        }}>
                            <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#ef4444',
                                backgroundColor: '#fef2f2',
                                borderRadius: '12px',
                                border: '1px solid #fecaca',
                                maxWidth: '500px',
                                width: '100%',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}>
                                <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', marginBottom: '1.5rem', color: '#ef4444' }}></i>
                                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: '600', color: '#dc2626' }}>Unable to Load Jobs</h3>
                                <p style={{ margin: '0 0 1.5rem 0', fontSize: '1rem', lineHeight: '1.5', color: '#7f1d1d' }}>{error}</p>
                                <button 
                                    onClick={() => window.location.reload()} 
                                    style={{
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#dc2626';
                                        e.target.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#ef4444';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <i className="fas fa-redo" style={{ marginRight: '0.5rem' }}></i>
                                    Try Again
                                </button>
                            </div>
                        </div>
                    ) : shouldShowEmpty ? (
                        // Show no jobs message when API returns empty results (only when not loading)
                        <div style={{
                            textAlign: 'center',
                            padding: '3rem 2rem',
                            color: '#6b7280',
                            backgroundColor: '#f9fafb',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            maxWidth: '500px',
                            width: '100%'
                        }}>
                            <i className="fas fa-search" style={{ fontSize: '3rem', marginBottom: '1.5rem', color: '#9ca3af' }}></i>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>No jobs found</h3>
                            <p style={{ fontSize: '1rem', lineHeight: '1.5', margin: '0', color: '#6b7280' }}>
                                {isSearchActive
                                    ? "No jobs match your current filters. Try adjusting your search or clearing filters."
                                    : "We couldn't find any jobs at the moment. Please try again later or change your location."}
                            </p>
                        </div>
                    ) : (
                        // Fallback: show nothing to avoid misleading 'Loading...'
                        null
                    )}
                </div>
            </div>


        </>
    );
}

export default FeaturedJobs;
